import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { partnerService } from '../../services/partner';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../state/auth/AuthProvider';
import { useLanguage } from '../../state/language/LanguageProvider';

const AcceptInvitationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    password: '',
    confirm_password: ''
  });

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      toast.error(t('partner.invalidInvitationLink'));
      navigate('/');
      return;
    }

    // For now, we'll skip validation and let the backend handle it
    // In a real implementation, you might want to validate the token first
  }, [token, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      toast.error(t('partner.invalidInvitationLink'));
      return;
    }

    if (formData.password !== formData.confirm_password) {
      toast.error(t('partner.passwordsDoNotMatch'));
      return;
    }

    if (formData.password.length < 6) {
      toast.error(t('partner.passwordTooShort', { minLength: '6' }));
      return;
    }

    try {
      setLoading(true);
      const response = await partnerService.acceptInvitation({
        token,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name
      });

      if (response.data?.token) {
        // Log the user in with the new token
        await login(response.data.token);
        toast.success(t('partner.invitationAcceptedSuccessfully'));
        navigate('/dashboard');
      }
    } catch (error: any) {
      console.error('Failed to accept invitation:', error);
      toast.error(error.response?.data?.error || t('partner.failedToAcceptInvitation'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-secondary-bg)]">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-[var(--color-surface-text)]">{t('partner.invalidInvitation')}</h2>
            <p className="mt-2 text-[var(--color-secondary-text)]">{t('partner.invitationLinkInvalid')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-secondary-bg)] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-[var(--color-info)]/10">
            <svg className="h-6 w-6 text-[var(--color-info)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-[var(--color-surface-text)]">
            {t('partner.acceptPartnerInvitation')}
          </h2>
          <p className="mt-2 text-center text-sm text-[var(--color-secondary-text)]">
            {t('partner.completeAccountSetup')}
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-[var(--color-secondary-text)]">
                  {t('partner.firstName')}
                </label>
                <input
                  id="first_name"
                  name="first_name"
                  type="text"
                  required
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-[var(--color-border)] placeholder-[var(--color-secondary-text)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] rounded-md focus:outline-none focus:ring-[var(--color-info)] focus:border-[var(--color-info)] focus:z-10 sm:text-sm"
                  placeholder={t('partner.firstNamePlaceholder')}
                />
              </div>
              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-[var(--color-secondary-text)]">
                  {t('partner.lastName')}
                </label>
                <input
                  id="last_name"
                  name="last_name"
                  type="text"
                  required
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-[var(--color-border)] placeholder-[var(--color-secondary-text)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] rounded-md focus:outline-none focus:ring-[var(--color-info)] focus:border-[var(--color-info)] focus:z-10 sm:text-sm"
                  placeholder={t('partner.lastNamePlaceholder')}
                />
              </div>
            </div>

            <div>
                              <label htmlFor="password" className="block text-sm font-medium text-[var(--color-secondary-text)]">
                  {t('partner.password')}
                </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-[var(--color-border)] placeholder-[var(--color-secondary-text)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] rounded-md focus:outline-none focus:ring-[var(--color-info)] focus:border-[var(--color-info)] focus:z-10 sm:text-sm"
                placeholder={t('partner.passwordPlaceholder')}
              />
            </div>

            <div>
                              <label htmlFor="confirm_password" className="block text-sm font-medium text-[var(--color-secondary-text)]">
                  {t('partner.confirmPassword')}
                </label>
              <input
                id="confirm_password"
                name="confirm_password"
                type="password"
                required
                value={formData.confirm_password}
                onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-[var(--color-border)] placeholder-[var(--color-secondary-text)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] rounded-md focus:outline-none focus:ring-[var(--color-info)] focus:border-[var(--color-info)] focus:z-10 sm:text-sm"
                placeholder={t('partner.confirmPasswordPlaceholder')}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-[var(--color-accent-text)] bg-[var(--color-info)] hover:bg-[var(--color-info)]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--color-info)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-[var(--color-accent-text)]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : null}
              {loading ? t('partner.acceptingInvitation') : t('partner.acceptInvitation')}
            </button>
          </div>

          <div className="text-center">
            <p className="text-xs text-[var(--color-secondary-text)]">
              {t('partner.termsAgreement')}
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AcceptInvitationPage;
